<script setup lang="ts">
import type { PdpEcommerceBlockStickyBarProps } from './PdpEcommerceBlockStickyBar.props'

withDefaults(defineProps<PdpEcommerceBlockStickyBarProps>(), {
  isVisible: false,
  isLoading: false,
  isDisabled: false,
})

const emit = defineEmits<{
  addToCart: []
}>()

const { formatPriceByLocale } = useFormatPrice()
const { currentCurrency } = useCurrency()

const isMounted = useMounted()

const stickyBarRef = ref<HTMLElement | null>(null)
const { height } = useElementSize(
  stickyBarRef,
  { width: 0, height: 0 },
  { box: 'border-box' }
)

const syncPowerfrontOffset = (nextHeight: number) => {
  if (!import.meta.client) return
  document.documentElement.style.setProperty(
    '--pdp-sticky-bar-height',
    `${nextHeight}px`
  )
}

watch(height, syncPowerfrontOffset)

onUnmounted(() => {
  syncPowerfrontOffset(0)
})
</script>

<template>
  <Teleport v-if="isMounted" to="body">
    <Transition name="sticky-bar">
      <div
        v-if="isVisible"
        id="pdp-ecommerce-sticky-bar"
        ref="stickyBarRef"
        class="bg-neutral-white border-primitives-black fixed inset-x-0 bottom-0 z-[55] hidden justify-center border-t lg:flex"
      >
        <div class="flex h-full w-full items-stretch p-4 pb-6">
          <div class="flex flex-1 items-center gap-3">
            <NuxtPicture
              v-if="image"
              provider="cloudinary"
              :src="image"
              :alt="title"
              sizes="52px"
              quality="70"
              :modifiers="{ aspectRatio: '1:1' }"
              fit="fill"
              class="border-primitives-grey-100 aspect-square w-[52px] shrink-0 border"
              :img-attrs="{ class: 'h-full w-full object-cover' }"
            />
            <div class="flex min-w-0 flex-col gap-1">
              <p class="text-book-6 truncate uppercase">{{ title }}</p>
              <div class="text-light-6 flex gap-x-2">
                <span
                  v-if="oldPrice"
                  class="text-primitives-grey-200 text-light-strike-6 line-through"
                >
                  {{ formatPriceByLocale(currentCurrency, oldPrice) }}
                </span>
                <span v-if="price">
                  {{ formatPriceByLocale(currentCurrency, price) }}
                </span>
                <span v-if="discount" class="text-medium-6">{{
                  discount
                }}</span>
              </div>
            </div>
          </div>
          <div class="h-full w-[240px] shrink-0">
            <MoleculesAddToBag
              class="h-full w-full"
              full-height
              :loading="isLoading"
              :disabled="isDisabled"
              data-testid="sticky-bar-add-to-cart"
              @click-handler="emit('addToCart')"
            />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
.sticky-bar-enter-active,
.sticky-bar-leave-active {
  transition: transform 0.3s ease-out;
}

.sticky-bar-enter-from,
.sticky-bar-leave-to {
  @apply translate-y-full;
}
</style>
